import { v4 as uuidv4 } from 'uuid';

import {
  AddScopeMessage,
  CreateNewConversation,
  RemoveScopeMessage,
  ReplaceScopeMessage,
  SetAuthoringDefaultState,
  SetConversationDefaultState,
  SetConversationFilters,
  SetConversationScope,
  SetCurrentConversationName,
  SetIsNewConversation,
  UpdateMessageReferences,
  UpdateSystemMessageTranslationData,
} from '~/constants/actions';
import {
  CHUNK_TYPE,
  DEFAULT_COLLECTION,
  EMPTY_CHAT,
  ERROR_MESSAGE,
  INITIAL_CONVERSATION,
  messageType,
  RESPONSE_TYPE,
  Role,
  WELCOME_AUTHORING_MESSAGE,
} from '~/constants/conversationModels';
import {
  GetConversationScope,
  GetCurrentConversation,
  GetCurrentConversationName,
  GetIsNewConversation,
  GetIsTyping,
  GetQueryValue,
  GetUserConversations,
} from '~/constants/getters';
import {
  AddChunk,
  AddChunkSSE,
  AddMessage,
  PopMessage,
  RemoveMessages,
  SetAllUserConversations,
  SetCurrentConversation,
  SetInitialConversation,
  SetIsMessageLoading,
  SetQueryValue,
  UpdateImageGenImage,
  UpdateMessageId,
  UpdateMessageSentiment,
} from '~/constants/mutations';

import { listConversations } from '~/services/api/conversations';

import store from '~/store'; // Import the Vuex store instance
import { processCitedReferences } from '~/utils/citation-messages';
import { applyEventToChunks } from '~/utils/new-agent-messages';

const defaultState = () => {
  return {
    conversations: [],
    isNewConversation: false,
    currentConversation: INITIAL_CONVERSATION,
    currentConversationName: '', //conversationFilters: {},
    isLoading: false,
    queryValue: '',
    conversationScope: {
      breadcrumb: [],
      collection: '',
      isScoped: false,
    },
  };
};

const state = defaultState();

const mutations = {
  [AddChunk](state, data) {
    if (state.currentConversation.conversationId !== data.currentConversationId) {
      return;
    }
    const messages = state.currentConversation.messages;
    const messageId = data.messageId;
    let isTyping = true;

    let messageIndex = messages.findIndex((msg) => msg.messageId === messageId);

    if (messageIndex > 0) {
      // Streaming: append chunk to existing message
      const existingMessage = messages[messageIndex];
      let messageText = existingMessage.message || '';
      let updatedData = existingMessage.data || [];
      let chunks = existingMessage.chunks ? [...existingMessage.chunks] : [];

      switch (data.responseType) {
        case RESPONSE_TYPE.messages: {
          const chunkContent = data.message?.chunk || '';
          messageText += chunkContent; // keep legacy message

          const lastChunk = chunks[chunks.length - 1];
          if (lastChunk?.type === CHUNK_TYPE.TEXT) {
            lastChunk.content += chunkContent;
          } else {
            chunks.push({ type: CHUNK_TYPE.TEXT, content: chunkContent });
          }
          break;
        }

        case RESPONSE_TYPE.data: {
          const blockData = data.data || [];
          const documentListBlock = blockData.find((block) => block.type === 'documentList');
          if (documentListBlock) {
            updatedData = processCitedReferences(
              chunks.map((c) => (c.type === CHUNK_TYPE.TEXT ? c.content : '')).join(''),
              blockData,
              false,
            );
          } else {
            updatedData = [...(existingMessage.data || []), ...blockData];
          }
          isTyping = false;
          break;
        }

        case RESPONSE_TYPE.systemMessages:
          if (data.message) {
            messageText += data.message;
            const lastChunk = chunks[chunks.length - 1];
            if (lastChunk?.type === CHUNK_TYPE.TEXT) {
              lastChunk.content += data.message;
            } else {
              chunks.push({ type: CHUNK_TYPE.TEXT, content: data.message });
            }
          }
          state.isLoading = false;
          isTyping = false;
          break;

        case RESPONSE_TYPE.endMessage:
          state.isLoading = false;
          isTyping = false;
          break;

        case RESPONSE_TYPE.error:
          state.isLoading = false;
          isTyping = false;
          messageText = ERROR_MESSAGE;
          chunks.push({ type: CHUNK_TYPE.TEXT, content: ERROR_MESSAGE });
          break;

        default:
          console.log('Invalid type of message');
      }

      messages[messageIndex] = {
        ...existingMessage,
        message: messageText,
        isTyping,
        data: updatedData,
        messageType: data.messageType,
        chunks,
      };
    } else {
      // New message (user-sent or initial)
      state.isLoading = false;

      const content =
        RESPONSE_TYPE.systemMessages === data.responseType ? data.message : data.message?.chunk || data.message || ''; // handle non-chunk messages

      const chunks = [];
      if (content) {
        chunks.push({ type: CHUNK_TYPE.TEXT, content });
      }

      const newMessage = {
        ...data,
        role: data.role || Role.chatbot,
        message: content, // keep legacy field
        chunks,
        data: data.data || [],
        messageType: data.messageType,
        isTyping: RESPONSE_TYPE.systemMessages !== data.responseType,
      };

      state.currentConversation = {
        ...state.currentConversation,
        messages: [...messages, newMessage],
      };
    }
  },

  [AddMessage](
    state,
    {
      data,
      currentCollection,
      responseMessageId,
      versionContentId,
      promptCardVariables,
      promptCardTitle,
      filters,
      remixImageId,
      userMessageId,
    },
  ) {
    const messagesToAdd = [];

    // Add user message if `data` is present (authoring cases may skip it)
    if (data) {
      messagesToAdd.push({
        ...EMPTY_CHAT,
        messageId: userMessageId || uuidv4(), // caller can pass its own id to reference this message later (e.g. to roll it back)
        message: data,
        isUser: true,
        role: Role.user,
        ...currentCollection,
        versionContentId,
        promptCardVariables,
        promptCardTitle,
        filters,
        ...(remixImageId && {
          data: [
            {
              type: 'imageBlock',
              image_ids: [remixImageId],
            },
          ],
        }), // add image_ids to user message if remixImageId is present
      });
    }

    // Add a placeholder message to receive streamed chunks
    messagesToAdd.push({
      ...EMPTY_CHAT,
      messageId: responseMessageId, // used to match future chunks
      isTyping: true,
      ...currentCollection,
    });
    // Update current conversation with new messages
    state.currentConversation = {
      ...state.currentConversation,
      messages: [...state.currentConversation.messages, ...messagesToAdd],
    };
  },

  [UpdateMessageSentiment](state, { messageId, sentiment }) {
    const messages = state.currentConversation.messages;
    const targetIndex = messages.findIndex((msg) => msg.messageId === messageId);

    if (targetIndex === -1) {
      return;
    }

    messages[targetIndex] = { ...messages[targetIndex], liked: sentiment };
  },

  [UpdateMessageReferences](state, { messageId, data }) {
    const messages = state.currentConversation.messages;
    const targetIndex = messages.findIndex((msg) => msg.messageId === messageId);

    if (targetIndex === -1) {
      return;
    }

    const existingMessage = messages[targetIndex];

    // New-agent SSE flow: references live as a chunk in `chunks`, not as a legacy block in `data`.
    if (data.type === CHUNK_TYPE.REFERENCES && existingMessage.chunks?.length) {
      messages[targetIndex] = {
        ...existingMessage,
        chunks: existingMessage.chunks.map((chunk) =>
          chunk.type === CHUNK_TYPE.REFERENCES ? { ...chunk, data } : chunk,
        ),
      };
      return;
    }

    messages[targetIndex] = {
      ...existingMessage,
      data: [data],
    };
  },

  [UpdateSystemMessageTranslationData](state, { messageId, data }) {
    const messages = state.currentConversation.messages;
    const messageIndex = messages.findIndex((message) => message.messageId === messageId);

    if (messageIndex < 0) {
      return;
    }

    const existingMessage = messages[messageIndex];

    // New-agent turns/events flow: translation lives as a chunk in `chunks`, not the legacy
    // `data` block — same distinction UpdateMessageReferences already draws for references.
    if (existingMessage.chunks?.length) {
      messages[messageIndex] = {
        ...existingMessage,
        chunks: existingMessage.chunks.map((chunk) =>
          chunk.type === CHUNK_TYPE.TRANSLATION ? { ...chunk, data: data[0] } : chunk,
        ),
      };
      return;
    }

    messages[messageIndex] = { ...existingMessage, data };
  },

  [SetConversationScope](state, data) {
    state.conversationScope = {
      ...state.conversationScope,
      ...data,
    };
  },

  // this is used to add the scoped message, will render ScopedMessage Component instead of Message
  [AddScopeMessage](state, message) {
    state.currentConversation = {
      ...state.currentConversation,
      messages: [
        ...state.currentConversation.messages,
        {
          ...message,
          messageType: messageType.context, // scopedMessage: true,
        },
      ],
    };
  },
  [ReplaceScopeMessage](state, message) {
    const newMessages = [...state.currentConversation.messages];
    newMessages[newMessages.length - 1] = {
      ...message,
      messageType: messageType.context,
    };
    state.currentConversation.messages = newMessages;
  },

  [RemoveScopeMessage](state) {
    state.currentConversation.messages.pop();
  },

  [SetCurrentConversation](state, conversation) {
    state.currentConversation = conversation;
  },
  [SetCurrentConversationName](state, data) {
    const currentConversationIndex = state.conversations.findIndex(
      (conv) => conv.conversationId === data.conversationId,
    );

    state.currentConversationName = data.new_title || '';
    if (currentConversationIndex > -1) {
      state.conversations[currentConversationIndex] = {
        ...state.conversations[currentConversationIndex],
        title: data.new_title || '',
      };
    }
  },
  [SetQueryValue](state, value) {
    state.queryValue = value;
  },
  [SetIsMessageLoading](state, isLoading) {
    state.isLoading = isLoading;
  },

  [SetAllUserConversations](state, data) {
    state.conversations = data;
  },
  [CreateNewConversation](state, { conversationId, isAuthoring = false, userId }) {
    state.currentConversation = {
      ...state.currentConversation,
      conversationId,
      userId,
      timestamp: Date.now(),
      messages: isAuthoring ? [WELCOME_AUTHORING_MESSAGE] : [],
    };
    state.conversations = [state.currentConversation, ...state.conversations];
  },
  [SetConversationFilters](state, { key, value }) {
    if (!value) {
      delete state.conversationFilters[key];
    } else {
      state.conversationFilters = { ...state.conversationFilters, [key]: value };
    }
  },
  [SetIsNewConversation](state, data) {
    state.isNewConversation = data;
  },
  [SetConversationDefaultState](state) {
    store.dispatch(`authoringStore/${SetAuthoringDefaultState}`);
    Object.assign(state, defaultState());
  },
  [PopMessage](state, messageId) {
    const messages = state.currentConversation.messages;
    const index = messages.findIndex((msg) => msg.messageId === messageId);
    const messageObject = messages[index];

    if (index !== -1) {
      const newMessage = {
        ...messageObject,
        message: '',
        chunks: [],
        data: [],
        isTyping: true,
      };

      // Replace the message in place and update the array reference for reactivity
      messages[index] = newMessage;
      state.currentConversation.messages = [...messages];
    }
  },

  /**
   * Drops messages entirely (rather than clearing them in place like PopMessage).
   * Used to roll back a turn that the backend rejected before persisting anything
   * (e.g. a guardrails 422 on stream/goto) so it doesn't linger in the chat history.
   *
   * @param {string[]} messageIds
   */
  [RemoveMessages](state, messageIds) {
    state.currentConversation.messages = state.currentConversation.messages.filter(
      (msg) => !messageIds.includes(msg.messageId),
    );
  },

  /**
   * One image slot in an IMAGE_GEN chunk's grid.
   * @typedef {{ id: string, status: 'loading'|'finished'|'error', url: string|null, params?: object }} ImageGenImage
   */

  // Separate from AddChunk because AddChunk handles the Ably message envelope shape.
  // SSE events arrive with a simpler { chunkType, data } structure so just want them on their own path
  /**
   * Append or update a chunk on a message from an SSE event.
   * For IMAGE_GEN, `data` is an ImageGenEvent (see useConversations):
   * image-started pushes a loading ImageGenImage, image-finished flips it to
   * finished, anything else flips it to error. The url arrives later via
   * UpdateImageGenImage once the image has been fetched.
   *
   * @param {{ chunkType: string, messageId: string, currentConversationId: string, data: object }} payload
   */
  [AddChunkSSE](state, { chunkType, messageId, currentConversationId, data }) {
    if (state.currentConversation.conversationId !== currentConversationId) return;

    const messages = state.currentConversation.messages;
    const messageIndex = messages.findIndex((msg) => msg.messageId === messageId);
    if (messageIndex < 0) return;

    const existingMessage = messages[messageIndex];
    const chunks = applyEventToChunks(existingMessage.chunks ? [...existingMessage.chunks] : [], chunkType, data);

    if (chunkType === CHUNK_TYPE.INTERRUPT) {
      // Stream pauses here waiting for the users HITL decision
      state.isLoading = false;
    }

    messages[messageIndex] = { ...existingMessage, chunks };
    state.currentConversation.messages = [...messages];
  },

  /**
   * Patch the fetched url onto a finished ImageGenImage in the message's IMAGE_GEN chunk.
   *
   * @param {{ messageId: string, currentConversationId: string, imageId: string, image: { url: string } }} payload
   */
  [UpdateImageGenImage](state, { messageId, currentConversationId, imageId, image }) {
    if (state.currentConversation.conversationId !== currentConversationId) return;
    const messages = state.currentConversation.messages;
    const messageIndex = messages.findIndex((msg) => msg.messageId === messageId);
    if (messageIndex < 0) return;

    const existingMessage = messages[messageIndex];
    const chunks = existingMessage.chunks ? [...existingMessage.chunks] : [];
    const chunkIdx = chunks.findIndex((c) => c.type === CHUNK_TYPE.IMAGE_GEN);
    if (chunkIdx < 0) return;

    const chunk = chunks[chunkIdx];
    const images = chunk.images.map((img) => (img.id === imageId ? { ...img, url: image.url } : img));
    chunks[chunkIdx] = { ...chunk, images };
    messages[messageIndex] = { ...existingMessage, chunks };
    state.currentConversation.messages = [...messages];
  },

  /**
   * Swaps a message's client-generated placeholder id for the real turn id the server assigns,
   * once it arrives via the `turn` event's `turnId`, and records that event's `status`
   * ('pending' | 'stored')
   *
   * @param {{ oldMessageId: string, newMessageId: string, status?: string, currentConversationId: string }} payload
   */
  [UpdateMessageId](state, { oldMessageId, newMessageId, status, currentConversationId }) {
    if (state.currentConversation.conversationId !== currentConversationId) return;
    const messages = state.currentConversation.messages;
    const messageIndex = messages.findIndex((msg) => msg.messageId === oldMessageId);
    if (messageIndex < 0) return;

    messages[messageIndex] = { ...messages[messageIndex], messageId: newMessageId, turnStatus: status };
    state.currentConversation.messages = [...messages];
  },
};

const getters = {
  [GetCurrentConversation](state) {
    return state.currentConversation;
  },
  [GetCurrentConversationName](state) {
    return state.currentConversationName;
  },
  [GetUserConversations](state) {
    return state.conversations;
  },
  [GetConversationScope](state) {
    return state.conversationScope;
  },
  [GetIsTyping](state) {
    return state.isLoading;
  },
  [GetQueryValue](state) {
    return state.queryValue;
  },
  [GetIsNewConversation](state) {
    return state.isNewConversation;
  },
};

const actions = {
  [AddMessage]: ({ commit }, data) => {
    commit(AddMessage, data);
    commit(SetIsMessageLoading, true);
  },
  [AddChunk]: async ({ commit }, data) => {
    commit(AddChunk, data);
  },
  [AddChunkSSE]: ({ commit }, data) => {
    commit(AddChunkSSE, data);
  },
  [SetCurrentConversation]: async ({ commit, state }, data) => {
    if (state.isLoading) {
      commit(SetIsMessageLoading, false);
    }
    commit(SetCurrentConversation, data);
  },

  [SetInitialConversation]: ({ commit }) => {
    commit(SetIsMessageLoading, false);
    commit(SetCurrentConversation, {
      ...INITIAL_CONVERSATION,
    });
    commit(SetConversationScope, defaultState().conversationScope);
  },
  [SetCurrentConversationName]: async ({ commit }, data) => {
    commit(SetCurrentConversationName, data);
  },
  [SetConversationScope]: ({ commit, state }, data) => {
    const lastMessage = state.currentConversation.messages[state.currentConversation.messages.length - 1];
    const prevLastMessage = state.currentConversation.messages[state.currentConversation.messages.length - 2];
    let { collection, isScoped } = data;

    // need this in case scope does not change, breadcrumbs changes in collectionsPanelDialog
    if (!collection) {
      commit(SetConversationScope, data);
      return;
    }

    // Corner Cases
    // no messages but scoped
    // this case is needed when user creates a new conversation and he scoped that

    if (!lastMessage && isScoped) {
      commit(AddScopeMessage, collection);
      commit(SetConversationScope, data);
      return;
    } else if (state.currentConversation.messages.length === 1 && !isScoped) {
      commit(RemoveScopeMessage);
      commit(SetConversationScope, data);
      return;
    } else if (!lastMessage && !isScoped) {
      commit(SetConversationScope, data);
      return;
    }

    // split in two cases opens collection panel (scope conversation) and close collections panel (back to vault)

    if (isScoped) {
      // add new scope message when last message is a normal message scoped in a different collection than the current
      if (lastMessage.messageType !== messageType.context && lastMessage.collectionId !== collection.collectionId) {
        commit(AddScopeMessage, collection);
        // replace scope message (when the last one is a scope message) , this case appear when user changed collection (new scope)
      } else if (
        lastMessage.messageType === messageType.context &&
        prevLastMessage.collectionId !== collection.collectionId
      ) {
        commit(ReplaceScopeMessage, collection);
        // this is the case when last scope message is 'Now talking to vault' && prevLastMessage.collectionId === collection.collectionId
      } else if (
        lastMessage.messageType === messageType.context &&
        lastMessage.collectionId === DEFAULT_COLLECTION.collectionId
      ) {
        commit(RemoveScopeMessage);
      }
    } else {
      // 3 conditions for default collection (vault) when we close

      // remove last scope message if no message was sent during scoping and the previous message is scoped to vault
      if (
        lastMessage.messageType === messageType.context &&
        prevLastMessage.collectionId === DEFAULT_COLLECTION.collectionId
      ) {
        commit(RemoveScopeMessage);
        // replace last scope message if no message was sent during scoping and the previous message is not scoped to vault
      } else if (
        lastMessage.messageType === messageType.context &&
        prevLastMessage.collectionId !== DEFAULT_COLLECTION.collectionId
      ) {
        commit(ReplaceScopeMessage, DEFAULT_COLLECTION);
        // add scope message ('Now talking to vault') if previous message was not scoped to vault
      } else if (
        lastMessage.messageType !== messageType.context &&
        lastMessage.collectionId !== DEFAULT_COLLECTION.collectionId
      ) {
        commit(AddScopeMessage, DEFAULT_COLLECTION);
      }
    }

    commit(SetConversationScope, data);
  },
  [SetQueryValue]: ({ commit }, value) => {
    commit(SetQueryValue, value);
  },
  [SetIsNewConversation]: ({ commit }, value) => {
    commit(SetIsNewConversation, value);
  },
  [SetAllUserConversations]: async ({ commit }, useNewAgent = false) => {
    try {
      const response = await listConversations(useNewAgent);
      commit(SetAllUserConversations, response.data);
    } catch (error) {
      console.error('Error fetching user conversations:', error);
      // Handle error here
    }
  },

  [CreateNewConversation]: ({ commit }, data) => {
    commit(CreateNewConversation, data);
  },
  [UpdateMessageSentiment]: ({ commit }, { messageId, sentiment }) => {
    commit(UpdateMessageSentiment, { messageId, sentiment });
  },
  [UpdateMessageReferences]: ({ commit }, { messageId, data }) => {
    commit(UpdateMessageReferences, { messageId, data });
  },
  [UpdateSystemMessageTranslationData]: ({ commit }, { messageId, data }) => {
    commit(UpdateSystemMessageTranslationData, { messageId, data });
  },
  [SetConversationDefaultState]: ({ commit }) => {
    commit(SetConversationDefaultState);
  },
  [PopMessage]: ({ commit }, messageId) => {
    commit(PopMessage, messageId);
  },
  [RemoveMessages]: ({ commit }, messageIds) => {
    commit(RemoveMessages, messageIds);
  },
};

export default {
  namespaced: true, // Important for namespacing module
  state,
  mutations,
  actions,
  getters,
};
